import { TierLimits, AddEntityModalConfig } from '../types';

// Invite-code gate. True routes anyone not yet approved to the beta screen
// (BETA_INVITE_CODE) before sign-in and again after it. False lets everyone
// straight through and marks them approved.
export const BETA_GATE_ENABLED = false;

// Checkout gate. Off shows "Pro coming soon" and disables the Pro button.
// Do NOT flip this on until
// every item under "Configuration and verification" in docs/PRICING.md is done.
// Checkout fails outright until STRIPE_PRICE_PRO is set on the function side,
// and automatic_tax fails session creation until Stripe Tax is activated.
export const PAYMENTS_ENABLED = true;

// Google API Configuration
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
export const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/drive.file';
export const GOOGLE_DISCOVERY_DOC = 'https://sheets.googleapis.com/$discovery/rest?version=v4';

// Stripe Configuration. The Pro price ID lives only on the server
// (STRIPE_PRICE_PRO in create-checkout-session), so the client can never pick
// what it gets charged.
export const SUPABASE_FUNCTIONS_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`;

// Pricing: the single source of truth for every price shown in the UI.
// Never hardcode a price string anywhere else. Pro is a one-time purchase.
export const PRICING = {
  pro: 79,
  // Early adopter price, shown alongside `pro` (struck through), never instead
  // of it. Enforced by a Stripe coupon with a private redemption cap. Never
  // state the cap or a count anywhere in the UI or copy.
  launch: 49,
} as const;

export const formatPrice = (amount: number): string =>
  `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;

// Money-back window, in days. Refunds are issued by hand in Stripe; the
// webhook drops the account back to Free when a refund goes through.
export const REFUND_DAYS = 30;

// Local preview only: forces the early adopter price treatment on without
// Stripe, so the pricing page can be reviewed before the coupon exists.
// Hard-disabled in production builds.
export const LAUNCH_PRICING_PREVIEW =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_LAUNCH_PRICING_PREVIEW === 'true';

// Beta
export const BETA_INVITE_CODE = process.env.REACT_APP_BETA_INVITE_CODE || '';

// Local Storage Keys
export const STORAGE_KEYS = {
  SHEET_ID: 'tallyup_sheet_id',
  PAYMENT_TIER: 'tallyup_payment_tier',
  UPGRADE_INTENT: 'tallyup_upgrade_intent',
  GOOGLE_ACCESS_TOKEN: 'tallyup_google_access_token',
  BETA_APPROVED: 'tallyup_beta_approved',
  DARK_MODE: 'tallyup_dark_mode',
  TUTORIAL_COMPLETED: 'tallyup_tutorial_completed',
  STORAGE_CONSENT: 'tallyup_storage_consent',
  TERMS_ACCEPTED: 'tallyup_terms_accepted',
} as const;

// Tier Limits
export const TIER_LIMITS: Record<'free' | 'pro', TierLimits> = {
  free: {
    bankAccounts: 5,
    investments: 5,
    retirement401kHoldings: 5,
    privateEquityHoldings: 5,
    cryptoHoldings: 5,
    // Free sees the last 12 months of net worth history. Pro sees all of it.
    // Truncation is display-only — every snapshot stays in the user's sheet.
    historyMonths: 12,
    // Free charts the first 5 years of the retirement projection and sees the
    // headline numbers; Pro sees the full 50-year trajectory and the
    // year-by-year table. The projection itself always runs on real data —
    // this gates depth, not access.
    projectionYears: 5,
  },
  pro: {
    bankAccounts: Infinity,
    investments: Infinity,
    retirement401kHoldings: Infinity,
    privateEquityHoldings: Infinity,
    cryptoHoldings: Infinity,
    historyMonths: Infinity,
    projectionYears: Infinity,
  },
};

// The five capped categories all share one number by design. Every piece of
// user-facing copy about the cap derives from here, so the number can never
// drift between the pricing page and what the app actually enforces.
export const FREE_ITEM_CAP = TIER_LIMITS.free.bankAccounts;
export const FREE_ITEM_CAP_WORD = (
  { 1: 'One', 2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five', 6: 'Six', 7: 'Seven',
    8: 'Eight', 9: 'Nine', 10: 'Ten' } as Record<number, string>
)[FREE_ITEM_CAP] ?? String(FREE_ITEM_CAP);

// Investment Account Types
export const INVESTMENT_ACCOUNT_TYPES = ['Individual', 'Roth IRA', 'Traditional IRA', 'SIMPLE IRA', '529 Plan', 'HSA', 'Money Market'];

// Private Equity Account Types
export const PRIVATE_EQUITY_ACCOUNT_TYPES = ['Individual', 'Trust', 'LLC', 'Other'];

// Private Equity Headers
export const PRIVATE_EQUITY_HEADERS = ['Company Name', 'Symbol', 'Shares', 'Price Per Share', 'Total Value', 'Cost Basis', 'Account'];

/** Investments sheet columns A–K */
export const INVESTMENT_HEADERS = [
  'Symbol',
  'Shares',
  'Cost Basis',
  'Current Price',
  'Total Value',
  'Return %',
  'Account',
  '1D %',
  '1W %',
  '1M %',
  '1Y %',
];

/** GOOGLEFINANCE formulas for Investments cols H–K.
 * Only 1D (changepct) works via the Sheets API. Historical windows return #N/A
 * when read through the API, so 1W/1M/1Y are filled client-side from Yahoo.
 */
export const buildInvestmentPeriodReturnFormulas = (rowIndex: number): [string, string, string, string] => [
  `=IFERROR(GOOGLEFINANCE(A${rowIndex},"changepct"),0)`,
  '', // 1W — filled client-side (Sheets API cannot read historical GOOGLEFINANCE)
  '',
  '',
];

// Crypto Account/Wallet Defaults
export const CRYPTO_ACCOUNT_DEFAULTS = ['Main', 'Coinbase', 'Kraken', 'Binance', 'Gemini', 'Robinhood', 'Ledger', 'MetaMask'];

// Default Data
export const DEFAULT_BANK_ACCOUNTS = [
  { name: 'Checking Account', balance: 0, editable: true, cell: 'Banking!B2' },
  { name: 'Savings Account 1', balance: 0, editable: true, cell: 'Banking!B3' },
  { name: 'Savings Account 2', balance: 0, editable: true, cell: 'Banking!B4' },
];

export const DEFAULT_CREDIT_CARDS = [
  { name: 'Credit Card 1', balance: 0, limit: 5000, editable: true, balanceCell: 'Credit Cards!B2', limitCell: 'Credit Cards!C2' },
  { name: 'Credit Card 2', balance: 0, limit: 5000, editable: true, balanceCell: 'Credit Cards!B3', limitCell: 'Credit Cards!C3' },
  { name: 'Credit Card 3', balance: 0, limit: 5000, editable: true, balanceCell: 'Credit Cards!B4', limitCell: 'Credit Cards!C4' },
  { name: 'Credit Card 4', balance: 0, limit: 5000, editable: true, balanceCell: 'Credit Cards!B5', limitCell: 'Credit Cards!C5' },
];

export const DEFAULT_ASSETS = [
  { name: 'Primary Home', value: 0, editable: true, cell: 'Assets!B2' },
  { name: 'Investment Portfolio', value: 0, editable: false, cell: 'Assets!B3' },
  { name: 'Vehicle 1', value: 0, editable: true, cell: 'Assets!B4' },
  { name: 'Vehicle 2', value: 0, editable: true, cell: 'Assets!B5' },
];

export const DEFAULT_HSA = [
  { name: 'HSA Account', balance: 0, editable: true, cell: 'HSA!B2' },
];

export const DEFAULT_LIABILITIES = [
  { name: 'Mortgage', value: 0, interestRate: 0, editable: true, cell: 'Liabilities!B2', interestRateCell: 'Liabilities!C2' },
  { name: 'Car Loan', value: 0, interestRate: 0, editable: true, cell: 'Liabilities!B3', interestRateCell: 'Liabilities!C3' },
  { name: 'Student Loans', value: 0, interestRate: 0, editable: true, cell: 'Liabilities!B4', interestRateCell: 'Liabilities!C4' },
  { name: 'Credit Card Debt', value: 0, interestRate: 0, editable: false, cell: 'Liabilities!B5', interestRateCell: 'Liabilities!C5' },
];

// Net Worth History, Budget, Goals Headers
export const NET_WORTH_HISTORY_HEADERS = ['Date', 'Net Worth', 'Total Assets', 'Total Liabilities', 'Banking', 'Investments', '401k', 'Credit Cards', 'Crypto'];
export const HSA_HEADERS = ['Account Name', 'Balance'];
export const CREDIT_SCORE_HEADERS = ['Bureau', 'Score'];
export const CHECK_INS_HEADERS = ['Date', 'Source'];
export const RETIREMENT_SETTINGS_HEADERS = ['Retirement Year', 'Annual Spending', 'Inflation Rate', 'Growth Outlook', 'Enabled Classes', 'Conservative BP', 'Optimistic BP', 'CAGR Overrides', 'Tax Rate', 'Birth Year'];
export const USER_SETTINGS_HEADERS = ['Dashboard View'];
export const DEFAULT_DASHBOARD_VIEW = 'standard';
export const DASHBOARD_VIEW_OPTIONS = [
  { value: 'overview', label: 'Overview', description: 'Summary cards and net worth trend' },
  { value: 'standard', label: 'Standard', description: 'Banking, credit cards, budget, and market data' },
  { value: 'full', label: 'Full', description: 'All widgets including investments, crypto, and more' },
] as const;
export const MARKET_SNAPSHOT_HEADERS = ['Symbol', 'Label', 'Price', 'Change', 'Change %'];
export const MARKET_SNAPSHOT_TICKERS: Array<{ symbol: string; label: string; gfSymbol: string }> = [
  { symbol: 'SPX', label: 'S&P 500', gfSymbol: '.INX' },
  { symbol: 'NDQ', label: 'Nasdaq', gfSymbol: '.IXIC' },
  { symbol: 'DJI', label: 'Dow Jones', gfSymbol: '.DJI' },
  { symbol: 'SPY', label: 'SPDR S&P 500', gfSymbol: 'SPY' },
  { symbol: 'QQQ', label: 'Invesco QQQ', gfSymbol: 'QQQ' },
  { symbol: 'VIX', label: 'CBOE Volatility', gfSymbol: 'INDEXCBOE:VIX' },
  { symbol: 'AAPL', label: 'Apple', gfSymbol: 'AAPL' },
  { symbol: 'MSFT', label: 'Microsoft', gfSymbol: 'MSFT' },
  { symbol: 'NVDA', label: 'NVIDIA', gfSymbol: 'NVDA' },
  { symbol: 'TSLA', label: 'Tesla', gfSymbol: 'TSLA' },
  { symbol: 'IBIT', label: 'Bitcoin ETF', gfSymbol: 'IBIT' },
  { symbol: 'GLD', label: 'Gold', gfSymbol: 'GLD' },
];
export const CREDIT_SCORE_BUREAUS = ['Equifax', 'TransUnion', 'Experian', 'FICO'];
export const CRYPTO_HEADERS = ['Symbol', 'CoinGecko ID', 'Amount', 'Cost Basis', 'Account'];
export const BUDGET_HEADERS = ['Category', 'Budgeted', 'Spent', 'Group'];
export const GOALS_HEADERS = ['Goal Name', 'Target Amount', 'Current Amount', 'Target Date', 'Unit'];

export const DEFAULT_BUDGET_CATEGORIES = [
  'Housing', 'Food & Dining', 'Transportation', 'Entertainment', 'Shopping',
  'Utilities', 'Healthcare', 'Personal', 'Education', 'Other',
];

export const HISTORY_LINE_COLORS = {
  netWorth: '#8b5cf6',
  assets: '#22c55e',
  liabilities: '#ef4444',
  /** Pine-style MA cloud: SMA rose, EMA blue (rose keeps SMA distinct from liabilities) */
  sma: '#f43f5e',
  ema: '#3b82f6',
  cloud: '#94a3b8',
  rsi: '#a855f7',
};

export const BUDGET_THRESHOLDS = {
  green: 0.7,
  yellow: 0.9,
};

// Modal Configurations
export const ADD_ENTITY_CONFIGS: Record<string, AddEntityModalConfig> = {
  investment: {
    title: 'Add Investment',
    entityType: 'investment',
    submitLabel: 'Add Investment',
    fields: [
      {
        name: 'symbol',
        label: 'Ticker Symbol',
        placeholder: 'e.g., TSLA',
        type: 'text',
        required: true,
        transform: (value) => value.toUpperCase(),
      },
      {
        name: 'shares',
        label: 'Number of Shares',
        placeholder: 'e.g., 10',
        type: 'number',
        required: true,
      },
      {
        name: 'costBasis',
        label: 'Cost Basis (per share)',
        placeholder: 'e.g., 250.00',
        type: 'number',
        required: true,
        step: '0.01',
      },
      {
        name: 'account',
        label: 'Account Type',
        placeholder: 'e.g., My Brokerage Account',
        type: 'select',
        options: INVESTMENT_ACCOUNT_TYPES,
        defaultValue: 'Individual',
        allowOther: true,
      },
    ],
  },
  bankAccount: {
    title: 'Add Bank Account',
    entityType: 'banking',
    submitLabel: 'Add Account',
    fields: [
      {
        name: 'name',
        label: 'Account Name',
        placeholder: 'e.g., Savings Account, Chase Checking',
        type: 'text',
        required: true,
      },
      {
        name: 'balance',
        label: 'Current Balance (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
        helpText: 'Leave blank to default to $0.00',
      },
    ],
  },
  creditCard: {
    title: 'Add Credit Card',
    entityType: 'creditCard',
    submitLabel: 'Add Card',
    fields: [
      {
        name: 'name',
        label: 'Card Name',
        placeholder: 'e.g., Amex Gold, Chase Sapphire',
        type: 'text',
        required: true,
      },
      {
        name: 'balance',
        label: 'Current Balance (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
      },
      {
        name: 'limit',
        label: 'Credit Limit (optional)',
        placeholder: '5000.00',
        type: 'number',
        step: '0.01',
        helpText: 'Defaults to $5,000 if left blank',
      },
    ],
  },
  asset: {
    title: 'Add Asset',
    entityType: 'asset',
    submitLabel: 'Add Asset',
    fields: [
      {
        name: 'name',
        label: 'Asset Name',
        placeholder: 'e.g., Rental Property, Boat, Jewelry',
        type: 'text',
        required: true,
      },
      {
        name: 'value',
        label: 'Current Value (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
        helpText: 'Leave blank to default to $0.00',
      },
    ],
  },
  hsa: {
    title: 'Add HSA Account',
    entityType: 'hsa',
    submitLabel: 'Add HSA Account',
    fields: [
      {
        name: 'name',
        label: 'Account Name',
        placeholder: 'e.g., Fidelity HSA, Optum HSA',
        type: 'text',
        required: true,
      },
      {
        name: 'balance',
        label: 'Current Balance (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
        helpText: 'Leave blank to default to $0.00',
      },
    ],
  },
  liability: {
    title: 'Add Liability',
    entityType: 'liability',
    submitLabel: 'Add Liability',
    fields: [
      {
        name: 'name',
        label: 'Liability Name',
        placeholder: 'e.g., Personal Loan, Medical Debt',
        type: 'text',
        required: true,
      },
      {
        name: 'value',
        label: 'Amount Owed (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
        helpText: 'Leave blank to default to $0.00',
      },
      {
        name: 'interestRate',
        label: 'APR % (optional)',
        placeholder: '0.0',
        type: 'number',
        step: '0.01',
        helpText: 'Annual percentage rate',
      },
    ],
  },
  retirement401k: {
    title: 'Add 401k Holding',
    entityType: 'retirement401k',
    submitLabel: 'Add Holding',
    fields: [
      {
        name: 'fundName',
        label: 'Fund Name',
        placeholder: 'e.g., Fidelity 500 Index Fund',
        type: 'text',
        required: true,
      },
      {
        name: 'ticker',
        label: 'Ticker (optional)',
        placeholder: 'e.g., FXAIX',
        type: 'text',
        transform: (value) => value.toUpperCase(),
      },
      {
        name: 'shares',
        label: 'Number of Shares (optional)',
        placeholder: 'e.g., 100',
        type: 'number',
        step: '0.001',
        helpText: 'Leave blank for trusts or accounts without share counts',
      },
      {
        name: 'balance',
        label: 'Current Balance',
        placeholder: 'e.g., 25000.00',
        type: 'number',
        required: true,
        step: '0.01',
      },
      {
        name: 'employer',
        label: 'Employer (optional)',
        placeholder: 'e.g., Acme Corp',
        type: 'text',
      },
      {
        name: 'accountType',
        label: 'Account Type',
        placeholder: '401k Traditional',
        type: 'text',
        defaultValue: '401k Traditional',
      },
      {
        name: 'costBasis',
        label: 'Cost Basis (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
        helpText: 'Total amount contributed',
      },
    ],
  },
  budgetCategory: {
    title: 'Add Budget Category',
    entityType: 'budgetCategory',
    submitLabel: 'Add Category',
    fields: [
      {
        name: 'category',
        label: 'Category Name',
        placeholder: 'e.g., Groceries, Subscriptions',
        type: 'text',
        required: true,
      },
      {
        name: 'budgeted',
        label: 'Monthly Budget',
        placeholder: '500.00',
        type: 'number',
        step: '0.01',
        required: true,
      },
      {
        name: 'group',
        label: 'Group',
        placeholder: 'e.g., Housing, Food, Transportation',
        type: 'text',
        helpText: 'Optional — categories with the same group are organized together',
      },
    ],
  },
  crypto: {
    title: 'Add Crypto Holding',
    entityType: 'crypto',
    submitLabel: 'Add Crypto',
    fields: [
      {
        name: 'symbol',
        label: 'Symbol',
        placeholder: 'e.g., BTC, ETH, SOL',
        type: 'text',
        required: true,
        transform: (value) => value.toUpperCase(),
      },
      {
        name: 'coinGeckoId',
        label: 'CoinGecko ID (optional)',
        placeholder: 'e.g., bitcoin, ethereum',
        type: 'text',
        helpText: 'Auto-detected for popular coins. Enter manually for obscure ones.',
      },
      {
        name: 'amount',
        label: 'Amount',
        placeholder: 'e.g., 0.5',
        type: 'number',
        required: true,
        step: '0.00000001',
      },
      {
        name: 'costBasis',
        label: 'Total Cost Basis',
        placeholder: 'e.g., 15000.00',
        type: 'number',
        step: '0.01',
        helpText: 'Total amount paid for this holding',
      },
      {
        name: 'account',
        label: 'Account / Wallet',
        placeholder: 'e.g., Coinbase, Ledger',
        type: 'select',
        options: CRYPTO_ACCOUNT_DEFAULTS,
        defaultValue: 'Main',
        allowOther: true,
      },
    ],
  },
  privateEquity: {
    title: 'Add Private Equity Holding',
    entityType: 'privateEquity',
    submitLabel: 'Add Holding',
    fields: [
      {
        name: 'name',
        label: 'Company Name',
        placeholder: 'e.g., SpaceX, Stripe',
        type: 'text',
        required: true,
      },
      {
        name: 'symbol',
        label: 'Symbol (optional)',
        placeholder: 'e.g., SPACEX',
        type: 'text',
        transform: (value) => value.toUpperCase(),
      },
      {
        name: 'shares',
        label: 'Number of Shares',
        placeholder: 'e.g., 100',
        type: 'number',
        required: true,
        step: '0.001',
      },
      {
        name: 'pricePerShare',
        label: 'Price Per Share',
        placeholder: 'e.g., 250.00',
        type: 'number',
        required: true,
        step: '0.01',
      },
      {
        name: 'costBasis',
        label: 'Total Cost Basis (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
        helpText: 'Total amount paid for these shares',
      },
      {
        name: 'account',
        label: 'Account Type',
        placeholder: '',
        type: 'select',
        options: PRIVATE_EQUITY_ACCOUNT_TYPES,
        defaultValue: 'Individual',
        allowOther: true,
      },
    ],
  },
  goal: {
    title: 'Add Financial Goal',
    entityType: 'goal',
    submitLabel: 'Add Goal',
    fields: [
      {
        name: 'name',
        label: 'Goal Name',
        placeholder: 'e.g., Emergency Fund, Down Payment',
        type: 'text',
        required: true,
      },
      {
        name: 'targetAmount',
        label: 'Target Amount',
        placeholder: '10000.00',
        type: 'number',
        step: '0.01',
        required: true,
      },
      {
        name: 'currentAmount',
        label: 'Current Amount (optional)',
        placeholder: '0.00',
        type: 'number',
        step: '0.01',
      },
      {
        name: 'targetDate',
        label: 'Target Date (optional)',
        placeholder: '2025-12-31',
        type: 'text',
        helpText: 'Format: YYYY-MM-DD',
      },
      {
        name: 'unit',
        label: 'Unit',
        placeholder: '',
        type: 'select',
        options: ['USD', 'BTC', 'ETH', 'Shares', 'Custom'],
        defaultValue: 'USD',
        allowOther: true,
      },
    ],
  },
  creditScore: {
    title: 'Add Credit Score',
    entityType: 'creditScore',
    submitLabel: 'Add Score',
    fields: [
      {
        name: 'bureau',
        label: 'Bureau',
        placeholder: '',
        type: 'select',
        options: CREDIT_SCORE_BUREAUS,
        required: true,
        defaultValue: 'Equifax',
      },
      {
        name: 'score',
        label: 'Score',
        placeholder: 'e.g., 750',
        type: 'number',
        required: true,
        step: '1',
        helpText: 'Score range: 300–850',
      },
    ],
  },
};

// TallyUp Theme Colors for PDF
export const PDF_COLORS = {
  purple: [147, 51, 234] as [number, number, number],
  pink: [236, 72, 153] as [number, number, number],
  green: [34, 197, 94] as [number, number, number],
  red: [239, 68, 68] as [number, number, number],
  gray: [107, 114, 128] as [number, number, number],
  lightGray: [243, 244, 246] as [number, number, number],
};

// Chart Colors
export const CHART_COLORS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
  '#06b6d4', '#6366f1', '#f97316', '#14b8a6', '#a855f7',
  '#ef4444', '#22c55e', '#eab308', '#84cc16', '#0ea5e9',
  '#d946ef', '#f43f5e', '#64748b', '#fb923c', '#4ade80',
];

// Spreadsheet Styling Colors
export const SHEET_COLORS = {
  purpleHeader: { red: 0.58, green: 0.4, blue: 0.8 },
  pinkAccent: { red: 0.9, green: 0.4, blue: 0.7 },
  white: { red: 1, green: 1, blue: 1 },
};
